/* ══════════════════════════════════════
   STORE 03 — AUTOMATIC FREE GIFTS
   One reconciler, a list of rules.

     rule = { prop, variantId, minSubtotal }   // minSubtotal in CENTS

     _free_gift    minSubtotal 0       any paid item in the cart
     _free_gift_2  minSubtotal 25000   cart subtotal reaches 250

   Invariant, re-enforced after every cart change:
     qualifies  = paidQty > 0 && paidSubtotal >= rule.minSubtotal
     qualifies  -> exactly ONE gift line for that rule
     !qualifies -> zero gift lines for that rule

   paidSubtotal EXCLUDES every gift line, so a gift can never help pay for the
   next gift. computeState() is the single source for it — the progress meter
   consumes the broadcast value rather than recomputing its own.

   Config comes from window.__freeGiftRules (see snippets/free-gift.liquid).
══════════════════════════════════════ */
(function () {
  'use strict';

  const CFG = window.__freeGiftRules || {};
  const MONEY_FORMAT = CFG.moneyFormat || '${{amount}}';

  const ALL_RULES = (CFG.rules || [])
    .map(function (r) {
      return {
        prop: r.prop,
        value: r.value || 'yes',
        enabled: r.enabled === true,
        variantId: Number(r.variantId) || 0,
        minSubtotal: Number(r.minSubtotal) || 0,
        title: r.title || '',
        name: r.name || r.title || 'Free gift',
        announce: r.announce !== false,
      };
    })
    .filter(function (r) {
      return !!r.prop;
    });

  // Every property this theme uses to tag a gift line — active or not. A line
  // carrying one of these that no ACTIVE rule claims is an orphan and gets
  // swept. That is what clears live carts when a merchant switches a promo off.
  const ALL_PROPS = ALL_RULES.map(function (r) {
    return r.prop;
  });

  function activeRules() {
    return ALL_RULES.filter(function (r) {
      return r.enabled && r.variantId > 0;
    });
  }

  /* ---------- routes ---------- */

  const ROUTES = window.routes || {};
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const BASE = ROOT.replace(/\/$/, '');
  const CART_JS = (ROUTES.cart_url || BASE + '/cart') + '.js';
  const ADD_URL = ROUTES.cart_add_url || BASE + '/cart/add';
  const CHANGE_URL = ROUTES.cart_change_url || BASE + '/cart/change';

  const CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  const CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/i;

  function urlOf(input) {
    if (!input) return '';
    if (typeof input === 'string') return input;
    if (typeof input.url === 'string') return input.url; // Request object
    try {
      return String(input);
    } catch (e) {
      return '';
    }
  }

  function isCartWrite(input) {
    return CART_WRITE_RE.test(urlOf(input));
  }

  function isCartAdd(input) {
    return CART_ADD_RE.test(urlOf(input));
  }

  // Our own cart writes go through the fetch that existed when this script ran,
  // so they cannot re-enter a later app's patch and bounce back at us.
  const nativeFetch = (function () {
    try {
      return window.fetch.bind(window);
    } catch (e) {
      return null;
    }
  })();

  /* ---------- section rendering (the theme's own repaint path) ---------- */

  const RENDER_TARGETS = {
    'cart-drawer': { host: '#CartDrawer', parse: '#CartDrawer' },
    'cart-icon-bubble': { host: '#cart-icon-bubble', parse: '.shopify-section' },
    'main-cart-items': { host: '#main-cart-items .js-contents', parse: '.js-contents' },
    'main-cart-footer': { host: '#main-cart-footer .js-contents', parse: '.js-contents' },
  };

  function sectionsOnPage() {
    return Object.keys(RENDER_TARGETS).filter(function (id) {
      return document.querySelector(RENDER_TARGETS[id].host);
    });
  }

  function withSections(body) {
    const ids = sectionsOnPage();
    if (ids.length) {
      body.sections = ids;
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  function getInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function renderSections(sections) {
    const wanted = sectionsOnPage();
    if (!sections) return { painted: false, complete: false };
    let painted = 0;
    wanted.forEach(function (id) {
      const html = sections[id];
      if (!html) return;
      const target = RENDER_TARGETS[id];
      const host = document.querySelector(target.host);
      if (!host) return;
      const inner = getInnerHTML(html, target.parse);
      if (inner != null) {
        host.innerHTML = inner;
        painted++;
      }
    });
    // complete === we drew every cart section this page has, so nothing else
    // needs to go and fetch them again.
    return { painted: painted > 0, complete: wanted.length > 0 && painted === wanted.length };
  }

  // Only Liquid draws the empty state, and it keys off a wrapper class rather
  // than the re-rendered contents — so toggle it the way Liquid would.
  function setEmptyState(isEmpty) {
    document.querySelectorAll('cart-drawer, cart-drawer-items').forEach(function (el) {
      el.classList.toggle('is-empty', isEmpty);
      const inner = el.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', isEmpty);
    });
    document.querySelectorAll('cart-items, #main-cart-footer').forEach(function (el) {
      el.classList.toggle('is-empty', isEmpty);
    });
  }

  // A line REMOVED must lose its row — never a reload. Section rendering
  // normally handles that, but a late-landing repaint from another component
  // can put a dead row back, so the DOM is checked against the cart after
  // every pass and any gift row the cart no longer has is dropped.
  //
  // The reverse direction is NOT symmetrical: only Liquid can draw an ADDED
  // row or the empty state, which is why those fall back to a reload instead.
  function pruneStaleGiftRows(cart) {
    const live = {};
    (cart.items || []).forEach(function (item) {
      if (!giftPropOf(item)) return;
      const title = (item.product_title || item.title || '').trim();
      live[title] = (live[title] || 0) + 1;
    });

    ['#main-cart-items', '#CartDrawer'].forEach(function (scope) {
      const root = document.querySelector(scope);
      if (!root) return;
      const seen = {};
      root.querySelectorAll('.cart-item--free-gift').forEach(function (row) {
        const nameEl = row.querySelector('.cart-item__name');
        const title = nameEl ? nameEl.textContent.trim() : '';
        seen[title] = (seen[title] || 0) + 1;
        if (seen[title] > (live[title] || 0)) row.remove();
      });
    });
  }

  // Fallback only — used when a mutation response came back without sections.
  // Each counter keeps its own text convention: the header bubble is blank at
  // 100+, the drawer count is always a bare number.
  function paintCounters(itemCount) {
    if (typeof itemCount !== 'number') return;
    document.querySelectorAll('.th-cart__count').forEach(function (el) {
      el.textContent = itemCount < 100 ? String(itemCount) : '';
    });
    document.querySelectorAll('.drawer__count').forEach(function (el) {
      el.textContent = String(itemCount);
    });
  }

  /* ---------- cart IO ---------- */

  function doFetch(url, options) {
    const f = nativeFetch || window.fetch;
    return f(url, options);
  }

  function getCart() {
    return doFetch(CART_JS, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin',
    }).then(function (res) {
      return res.json();
    });
  }

  function post(url, body) {
    return doFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json();
    });
  }

  function addGift(rule) {
    const properties = {};
    properties[rule.prop] = rule.value;
    return post(
      ADD_URL,
      withSections({ items: [{ id: rule.variantId, quantity: 1, properties: properties }] })
    );
  }

  function changeLine(key, quantity) {
    return post(CHANGE_URL, withSections({ id: key, quantity: quantity }));
  }

  /* ---------- state ---------- */

  // The line PROPERTY identifies a gift, never the variant id — that is what
  // lets a customer buy a product and also receive it free as two separate
  // lines.
  function giftPropOf(item) {
    const props = item && item.properties;
    if (!props) return null;
    for (let i = 0; i < ALL_PROPS.length; i++) {
      const key = ALL_PROPS[i];
      if (Object.prototype.hasOwnProperty.call(props, key)) {
        const v = props[key];
        if (v !== null && v !== undefined && v !== '') return key;
      }
    }
    return null;
  }

  // THE single definition of the qualifying subtotal. Every consumer — the
  // rules below and the progress meter — reads from here.
  function computeState(cart) {
    let paidSubtotal = 0;
    let paidQty = 0;
    (cart.items || []).forEach(function (item) {
      if (giftPropOf(item)) return; // a gift never helps pay for the next gift
      paidSubtotal += Number(item.final_line_price) || 0;
      paidQty += Number(item.quantity) || 0;
    });
    return { paidSubtotal: paidSubtotal, paidQty: paidQty, itemCount: cart.item_count || 0 };
  }

  // Compute the SINGLE next correction. The caller applies it, re-reads the
  // cart and asks again — never more than one cart write in flight.
  function nextCorrection(cart, state) {
    const active = activeRules();
    const activeProps = {};
    active.forEach(function (r) {
      activeProps[r.prop] = true;
    });

    const items = cart.items || [];

    // 1. Orphans: a gift property no active rule claims.
    for (let i = 0; i < items.length; i++) {
      const prop = giftPropOf(items[i]);
      if (prop && !activeProps[prop]) {
        return { type: 'remove', key: items[i].key, reason: 'orphan' };
      }
    }

    // 2. Per-rule invariant.
    for (let r = 0; r < active.length; r++) {
      const rule = active[r];
      const lines = items.filter(function (it) {
        return giftPropOf(it) === rule.prop;
      });
      const qualifies = state.paidQty > 0 && state.paidSubtotal >= rule.minSubtotal;

      if (!qualifies) {
        if (lines.length) {
          return { type: 'remove', key: lines[0].key, rule: rule, reason: 'unqualified' };
        }
        continue;
      }

      // Gift product changed under us — drop the stale variant, then re-add.
      const wrong = lines.filter(function (l) {
        return Number(l.variant_id) !== rule.variantId;
      });
      if (wrong.length) {
        return { type: 'remove', key: wrong[0].key, rule: rule, reason: 'wrong-variant' };
      }

      if (lines.length === 0) return { type: 'add', rule: rule, reason: 'missing' };
      if (lines.length > 1) {
        return { type: 'remove', key: lines[1].key, rule: rule, reason: 'duplicate' };
      }
      if (Number(lines[0].quantity) !== 1) {
        return { type: 'change', key: lines[0].key, quantity: 1, rule: rule, reason: 'clamp' };
      }
    }

    return null;
  }

  /* ---------- reconcile ---------- */

  const MAX_PASSES = 8;
  let running = false;
  let rerunRequested = false;

  function broadcast(state) {
    const rules = activeRules().map(function (rule) {
      return {
        prop: rule.prop,
        name: rule.name,
        title: rule.title,
        minSubtotal: rule.minSubtotal,
        qualifies: state.paidQty > 0 && state.paidSubtotal >= rule.minSubtotal,
      };
    });
    document.dispatchEvent(
      new CustomEvent('free-gift:state', {
        detail: {
          paidSubtotal: state.paidSubtotal,
          paidQty: state.paidQty,
          itemCount: state.itemCount,
          rules: rules,
          moneyFormat: MONEY_FORMAT,
        },
      })
    );
  }

  // Dispatch the theme's own cart event, built the way the theme builds it.
  //
  // When we have ALREADY painted every cart section, we announce it with the
  // source cart.js uses for its own post-render publish ('cart-items'), so
  // <cart-items> does not kick off a second, unordered
  // `?section_id=main-cart-items` fetch. Two of those in flight land in
  // arrival order rather than cart order, and the loser repaints stale rows —
  // which is what leaves a dead gift row on screen after the cart drops back
  // under the threshold.
  //
  // __freeGift is our own marker so this never re-enters our subscriber,
  // whatever source we used.
  function publishCartUpdate(cart, complete) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: complete ? 'cart-items' : 'free-gift',
          __freeGift: true,
          cartData: cart,
        });
      }
    } catch (e) {
      /* the theme's pubsub is optional */
    }
  }

  async function reconcile() {
    if (running) {
      rerunRequested = true;
      return;
    }
    running = true;
    try {
      let cart = await getCart();
      let state = computeState(cart);
      let lastResponse = null;
      let changed = false;
      const addedRules = [];

      for (let pass = 0; pass < MAX_PASSES; pass++) {
        const fix = nextCorrection(cart, state);
        if (!fix) break;

        let response;
        if (fix.type === 'add') {
          response = await addGift(fix.rule);
        } else {
          response = await changeLine(fix.key, fix.type === 'change' ? fix.quantity : 0);
        }
        // Shopify answers a refusal with { status: 4xx } — stop rather than spin.
        if (!response || response.status) break;

        changed = true;
        lastResponse = response;
        if (fix.type === 'add') addedRules.push(fix.rule);

        cart = await getCart();
        state = computeState(cart);
      }

      if (changed) {
        const render = renderSections(lastResponse && lastResponse.sections);
        setEmptyState(state.itemCount === 0);
        if (!render.painted) paintCounters(state.itemCount);
        publishCartUpdate(cart, render.complete);

        const settled = cart;
        pruneStaleGiftRows(settled);
        // Once more after the frame settles, in case another component's
        // in-flight repaint lands behind us.
        setTimeout(function () {
          pruneStaleGiftRows(settled);
        }, 400);

        const announced = addedRules
          .filter(function (rule) {
            return rule.announce;
          })
          .map(function (rule) {
            return rule.name || rule.title;
          })
          .filter(Boolean);
        if (announced.length) {
          // `title` stays for older listeners; `titles` carries the full set.
          document.dispatchEvent(
            new CustomEvent('free-gift:added', {
              detail: { title: announced[0], titles: announced },
            })
          );
        }

        // Last resort: we mutated the cart on the cart page but got no Liquid
        // back to draw the added rows / empty state with. Removals never need
        // this — the row is already gone from the re-rendered contents.
        if (!render.complete && document.querySelector('cart-items')) {
          if (addedRules.length > 0 || state.itemCount === 0) window.location.reload();
        }
      }

      // Broadcast on EVERY pass, including passes that changed nothing — a
      // plain quantity edit moves the meter without touching a gift line.
      broadcast(state);
    } catch (e) {
      console.warn('[free-gift] reconcile failed:', e);
    } finally {
      running = false;
      if (rerunRequested) {
        rerunRequested = false;
        setTimeout(reconcile, 0);
      }
    }
  }

  /* ---------- signals ---------- */

  const DEBOUNCE_MS = 150;
  const SWEEPS_MS = [1200, 3000];
  let debounceTimer = null;
  const sweepTimers = [];

  function schedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, DEBOUNCE_MS);
    scheduleSweeps();
  }

  // reconcile() is idempotent — one GET when there is nothing to fix — so a
  // signal that was swallowed by an app's patch self-heals on the next sweep.
  function scheduleSweeps() {
    while (sweepTimers.length) clearTimeout(sweepTimers.pop());
    SWEEPS_MS.forEach(function (ms) {
      sweepTimers.push(setTimeout(reconcile, ms));
    });
  }

  // 1. fetch — chain onto whatever is installed AT THIS MOMENT. App scripts
  // patch window.fetch too and whichever assigns last wins, so this is
  // re-asserted over time rather than installed once.
  function installFetchHook() {
    const current = window.fetch;
    if (typeof current !== 'function' || current.__fgHooked) return;
    const hooked = function (input) {
      const result = current.apply(this, arguments);
      try {
        if (isCartWrite(input)) {
          Promise.resolve(result)
            .then(function (res) {
              if (!res || typeof res.ok === 'undefined' || res.ok) schedule();
            })
            .catch(function () {});
        }
      } catch (e) {
        /* never break the page's own fetch */
      }
      return result;
    };
    hooked.__fgHooked = true;
    window.fetch = hooked;
  }

  // 2. XMLHttpRequest
  function installXhrHook() {
    const XHR = window.XMLHttpRequest;
    if (!XHR || !XHR.prototype || XHR.prototype.__fgHooked) return;
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      this.__fgUrl = url;
      return open.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      const xhr = this;
      try {
        if (isCartWrite(xhr.__fgUrl)) {
          xhr.addEventListener('loadend', function () {
            if (xhr.status >= 200 && xhr.status < 300) schedule();
          });
        }
      } catch (e) {
        /* never break the page's own XHR */
      }
      return send.apply(this, arguments);
    };
    XHR.prototype.__fgHooked = true;
  }

  // 3. form submit, capture phase — catches no-JS and app forms posting to
  // /cart/add before anything else can stop propagation.
  document.addEventListener(
    'submit',
    function (e) {
      const form = e.target;
      if (!form || !form.action) return;
      if (isCartAdd(form.action)) schedule();
    },
    true
  );

  // 4. the theme's own cart event (ours is tagged and ignored)
  function subscribeTheme(tries) {
    tries = tries || 0;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && (event.__freeGift || event.source === 'free-gift')) return;
        schedule();
      });
      return;
    }
    if (tries < 50) {
      setTimeout(function () {
        subscribeTheme(tries + 1);
      }, 100);
    }
  }

  // 5. tab return + bfcache restore
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      installFetchHook();
      schedule();
    }
  });

  window.addEventListener('pageshow', function (e) {
    installFetchHook();
    if (e.persisted) schedule();
  });

  function init() {
    installFetchHook();
    installXhrHook();
    subscribeTheme();
    reconcile();
    // Re-assert the fetch hook as late-loading app scripts install theirs.
    [0, 500, 1500, 3000, 6000].forEach(function (ms) {
      setTimeout(installFetchHook, ms);
    });
  }

  installFetchHook();
  installXhrHook();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ══════════════════════════════════════
     PROGRESS METER
     Reports the reconciler's settled state.
     It never decides eligibility itself.
  ══════════════════════════════════════ */

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    const p = precision == null ? 2 : precision;
    const t = thousands == null ? ',' : thousands;
    const d = decimal == null ? '.' : decimal;
    const number = (Number(cents) / 100.0).toFixed(p);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t);
    return dollars + (parts[1] ? d + parts[1] : '');
  }

  // Mirrors Liquid's money / money_with_currency filters using the shop's own
  // format string, so a JS re-render matches what Liquid printed beside it.
  function formatMoney(cents, format) {
    const fmt = format || MONEY_FORMAT;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholder);
    let value;
    switch (match && match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  window.__freeGiftFormatMoney = formatMoney;

  function escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  function renderMeter(meter, detail) {
    const prop = meter.getAttribute('data-fg-meter') || '_free_gift_2';
    const rule = (detail.rules || []).filter(function (r) {
      return r.prop === prop;
    })[0];

    // Rule switched off mid-session — stop reporting a promotion that is gone.
    if (!rule) {
      meter.hidden = true;
      return;
    }
    meter.hidden = false;

    const threshold = rule.minSubtotal || Number(meter.getAttribute('data-threshold')) || 0;
    const paid = detail.paidSubtotal;
    const remaining = Math.max(0, threshold - paid);
    const unlocked = rule.qualifies === true;

    // floor(), then hold at 99 until actually unlocked — round() turns 99.6%
    // into a full track sitting next to "$1.00 away".
    let pct = threshold > 0 ? Math.floor((paid / threshold) * 100) : 100;
    pct = Math.max(0, Math.min(100, pct));
    if (!unlocked && pct >= 100) pct = 99;
    if (unlocked) pct = 100;

    const wasUnlocked = meter.classList.contains('is-unlocked');
    meter.classList.toggle('is-unlocked', unlocked);
    meter.setAttribute('data-paid', String(paid));

    const fill = meter.querySelector('[data-fg-meter-fill]');
    if (fill) fill.style.width = pct + '%';

    const track = meter.querySelector('[data-fg-meter-track]');
    if (track) track.setAttribute('aria-valuenow', String(pct));

    const text = meter.querySelector('[data-fg-meter-text]');
    if (text) {
      const name = rule.name || meter.getAttribute('data-gift-name') || 'gift';
      if (unlocked) {
        text.innerHTML =
          'Unlocked. A free <strong>' + escapeHtml(name) + '</strong> is in your cart.';
      } else {
        text.innerHTML =
          '<strong>' +
          escapeHtml(formatMoney(remaining, detail.moneyFormat)) +
          '</strong> away from a free <strong>' +
          escapeHtml(name) +
          '</strong>.';
      }
    }

    // Animate the crossing only — never a page that loaded already unlocked.
    if (unlocked && !wasUnlocked && meter.classList.contains('is-ready')) {
      meter.classList.remove('fg-meter--pop');
      void meter.offsetWidth;
      meter.classList.add('fg-meter--pop');
    }
  }

  // The server-rendered first paint must not animate; transitions are enabled
  // one frame later, so only subsequent updates move.
  function markMetersReady() {
    document.querySelectorAll('[data-fg-meter]').forEach(function (meter) {
      if (meter.classList.contains('is-ready')) return;
      requestAnimationFrame(function () {
        meter.classList.add('is-ready');
      });
    });
  }

  document.addEventListener('free-gift:state', function (e) {
    document.querySelectorAll('[data-fg-meter]').forEach(function (meter) {
      renderMeter(meter, e.detail);
    });
    markMetersReady();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markMetersReady);
  } else {
    markMetersReady();
  }
})();
